import * as React from 'react'
import { useCallback } from 'react'
import { Formik } from 'formik'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { SideDrawerForm, toast } from '@sales-center/components'
import { INotification } from '@user/store/notifications/typings'
import { CollapsibleSection, NotificationLabel } from '../../atoms'
import { useFormDataOnMount } from './hooks'
import { NotificationFormModel } from './typings'
import { SalesTeamTree } from './SalesTeamTree'
import { AdditionalEmployees } from './AdditionalEmployees'
import { toNotificationSettingInput } from './constants'
import { updateNotification } from '@user/store/notifications/api'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'

export const ManageSlideout: React.FC<{
  notification: INotification
  onClose: () => void
}> = ({ notification, onClose }) => {
  const { loading, list, emails } = useFormDataOnMount(notification)
  const dispatch = useAppDispatch()

  const onSubmit = useCallback(
    async (model: NotificationFormModel) => {
      const { additionalEmailIds, salesTeamHierarchy } =
        toNotificationSettingInput(model)

      const hierarchyValid =
        salesTeamHierarchy.salesTeamIds.length > 0 ||
        salesTeamHierarchy.salesTeamPositions.length > 0

      const result = await dispatch(
        updateNotification({
          ...notification,
          additionalEmailIds:
            additionalEmailIds.length > 0 ? additionalEmailIds.join(',') : null,
          salesTeamHierarchy: hierarchyValid
            ? JSON.stringify(salesTeamHierarchy)
            : null,
          isEnabled:
            additionalEmailIds.length === 0 && !hierarchyValid
              ? false
              : notification.isEnabled,
        }),
      )

      if (updateNotification.fulfilled.match(result)) {
        toast.success('Notification successfully updated.')
        onClose()
      } else {
        toast.success(
          'An error occurred.  We are unable to update the Notification.',
        )
      }
    },
    [dispatch, notification, onClose],
  )

  return loading ? (
    <Stack mt={2} alignItems="center">
      <CircularProgress />
    </Stack>
  ) : (
    <Formik<NotificationFormModel>
      onSubmit={onSubmit}
      initialValues={{ list, additional: notification.additionalEmailIds }}
    >
      {({ submitForm }) => (
        <SideDrawerForm
          onSave={submitForm}
          onCancel={onClose}
          containerTop="55px"
          tidalMode
        >
          <Box paddingX={2} pt={0.9}>
            <NotificationLabel
              type="Internal"
              name={notification.notificationEventName}
              helperText={notification.helperText}
            />
          </Box>

          <Divider sx={{ margin: '8px 20px' }} />

          <CollapsibleSection
            title="Sales Teams"
            subtitle={
              'Teams, Positions, and/or Employees selected below will receive this notification. Inactive users will not receive any email notifications.'
            }
          >
            <SalesTeamTree />
          </CollapsibleSection>

          <CollapsibleSection
            title="Notify Non-Sales Center Employees"
            subtitle="Send this notification to employees without access to Sales Center."
            paddingTop={2}
          >
            <AdditionalEmployees options={emails} />
          </CollapsibleSection>
        </SideDrawerForm>
      )}
    </Formik>
  )
}
ManageSlideout.displayName = 'ManageSlideout'
